import { DateTime } from "luxon";
import { GrayCard } from "../../components/Card";
import { Box } from "../../components/Box";
import { Flex } from "../../components/Flex";
import { Paragraph } from "../../components/Paragraph";
import { ManualAcceptReject } from "../IndividualView/ManualAcceptReject";
import { checksStatusName } from "../IndividualView/helper";

/**
 * What the selected alert brought, and the sign-off it is asking for.
 *
 * The plain line about what signing off does is not decoration. The page now
 * carries two decisions that staff will confuse: accepting an alert records that
 * the new results have been reviewed and drives the monitoring record status,
 * while a match decision is a compliance position that persists and is not what
 * the alert is asking about.
 *
 * The sign-off itself — who, when, why — is shown by ScreeningResultView's own
 * decision banner (the same one the mandate Screening check uses), not here, so
 * it isn't rendered twice.
 */

interface AlertPanelProps {
  activity: any;
  returnedCount: number;
  totalMatchCount: number;
}

export const MonitoringAlertPanel = ({
  activity,
  returnedCount,
  totalMatchCount,
}: AlertPanelProps) => {
  const date = activity?.created_on
    ? DateTime.fromISO(activity.created_on).toFormat("d MMMM yyyy")
    : "-";
  const statusLabel =
    checksStatusName[activity?.status?.status] ??
    activity?.status?.status ??
    "-";
  const isInReview = activity?.status?.status === "review";

  return (
    <GrayCard mx="0px" mb={3}>
      <Flex justifyContent="space-between" alignItems="flex-start" gap="16">
        <Box flex={1}>
          <Paragraph mt={0} mb={0} fontWeight="600" color="gray.80">
            {activity?.event}  ·  {date}  ·  {statusLabel}
          </Paragraph>
          <Paragraph mt={1} mb={0} fontSize={1} color="gray.60">
            {returnedCount === 0
              ? "This alert returned no watchlist matches."
              : `Returned ${returnedCount} of the ${totalMatchCount} match${
                  totalMatchCount === 1 ? "" : "es"
                } on this record`}
          </Paragraph>
          {isInReview && (
            <Paragraph mt={2} mb={0} fontSize={0} color="gray.50">
              Signing off an alert records that you have reviewed what arrived.
              It does not change any match decision below.
            </Paragraph>
          )}
        </Box>

        {isInReview && (
          <Box flexShrink={0}>
            <ManualAcceptReject
              id={activity.id}
              title={activity?.event}
              isMonitoring={true}
              permissionCodename="monitoring.review_activity"
              permissionDeniedMessage="You don't have permission to accept or reject this monitoring alert"
            />
          </Box>
        )}
      </Flex>
    </GrayCard>
  );
};

interface RecordPanelProps {
  counts: {
    total: number;
    unknown: number;
    falsePositive: number;
    trueMatch: number;
  };
  alertCount: number;
  awaitingSignOff: number;
}

/** The All activity equivalent: the record's standing position. */
export const MonitoringRecordPanel = ({
  counts,
  alertCount,
  awaitingSignOff,
}: RecordPanelProps) => (
  <GrayCard mx="0px" mb={3}>
    <Paragraph mt={0} mb={0} fontWeight="600" color="gray.80">
      {counts.total} match{counts.total === 1 ? "" : "es"} on this record
    </Paragraph>
    <Paragraph mt={1} mb={0} fontSize={1} color="gray.60">
      {counts.unknown} Unknown  ·  {counts.trueMatch} True Match  ·{" "}
      {counts.falsePositive} False Positive
    </Paragraph>
    <Paragraph mt={1} mb={0} fontSize={0} color="gray.50">
      {alertCount} alert{alertCount === 1 ? "" : "s"}
      {awaitingSignOff > 0
        ? `  ·  ${awaitingSignOff} awaiting sign-off`
        : "  ·  none awaiting sign-off"}
    </Paragraph>
  </GrayCard>
);
